import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { asMetaAuthFailureReason } from '../../../../models/utils/metaAuthFailure';
import {
	useConnectMetaLeads,
	useDisconnectMetaLeads,
	useGetMetaAdAccounts,
	useGetMetaAdsProvider,
	useGetMetaPages,
	useReconnectMetaLeads,
} from '../../../../queries/Ads';
import { invalidateMetaAdAccounts } from '../../../../queries/Ads/useGetMetaAdAccounts';
import { invalidateMetaPages } from '../../../../queries/Ads/useGetMetaPages';
import { invalidateMetaAdsProvider } from '../../../../queries/Ads/useGetMetaAdsProvider';
import { Button } from '../../../components-v2/Button';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../components/ConfirmationDialog';
import { LoadingLogo } from '../../../components/LoadingLogo';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MetaAuthFailureNotice } from '../../../components/MetaAuthFailureNotice';
import { MetaMainLogo } from '../../../components/svgs/icons/MetaMainLogo';
import { bs } from '../../../styles/styles';
import { FacebookAdsModal } from '../FacebookAdsModal';
import { styleSheet as integrationsStyleSheet } from '../styles';
import { styleSheet } from './styles';

const DisconnectConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Disconnect Now',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

const ReconnectConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCta: true,
		representedObject: true,
		title: 'Reconnect',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

const ReconnectStalledMessage =
	'Facebook Ads was disconnected, but sign-in to Facebook could not start. Click Connect to Facebook to finish reconnecting.';

const isFacebookAuthUrl = (authUrl?: string) =>
	authUrl?.startsWith('https://www.facebook.com/') || authUrl?.startsWith('https://facebook.com/');

const ManageFacebookAds = () => {
	const history = useHistory();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('ManageFacebookAds');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isDisconnectConfirmationOpen, setIsDisconnectConfirmationOpen] = useState(false);
	const [isReconnectConfirmationOpen, setIsReconnectConfirmationOpen] = useState(false);

	const { data: metaAdsProvider, isPending } = useGetMetaAdsProvider({
		impersonationContext: userSession.impersonationContext,
	});

	const isConnected = metaAdsProvider?.enabled ?? false;
	const isConfigured =
		isConnected && (metaAdsProvider?.adAccountIds?.length ?? 0) > 0 && (metaAdsProvider?.pageIds?.length ?? 0) > 0;
	const reconnectReason = asMetaAuthFailureReason(metaAdsProvider?.reconnectReason);

	// Fetch ad accounts and pages (for display in configuration summary)
	const { data: adAccounts, isLoading: isLoadingAccounts } = useGetMetaAdAccounts({
		impersonationContext: userSession.impersonationContext,
		enabled: isConnected,
	});

	const { data: pages, isLoading: isLoadingPages } = useGetMetaPages({
		impersonationContext: userSession.impersonationContext,
		enabled: isConnected,
	});

	const onError = (operationName: string, error: Api.IOperationResultNoValue) => {
		errorMessages.push({
			messages: [`Failed to ${operationName}`],
		});
		logApiError(operationName, error);
	};

	const invalidateMetaAdsQueries = () => {
		invalidateMetaAdsProvider({ impersonationContext: userSession.impersonationContext });
		invalidateMetaAdAccounts({ impersonationContext: userSession.impersonationContext });
		invalidateMetaPages({ impersonationContext: userSession.impersonationContext });
	};

	const { mutate: connectMetaLeads, isPending: isConnecting } = useConnectMetaLeads({
		onError: error => onError('Connect', error),
		onSuccess: value => {
			if (isFacebookAuthUrl(value.authUrl)) {
				window.location.replace(value.authUrl);
			} else {
				onError('Connect', {
					success: false,
					systemMessage: 'Invalid OAuth URL received',
				} as Api.IOperationResultNoValue);
			}
		},
	});

	const { mutate: disconnectMetaLeads, isPending: isDisconnecting } = useDisconnectMetaLeads({
		onError: error => onError('Disconnect', error),
		onSuccess: () => {
			toast.success('Facebook Ads disconnected successfully!');
			// Invalidate all Meta Ads queries
			invalidateMetaAdsQueries();
			history.push('/integrations');
		},
	});

	const onReconnectStalled = (error: Api.IOperationResultNoValue) => {
		errorMessages.push({ messages: [ReconnectStalledMessage] });
		logApiError('Reconnect', error);
	};

	const { mutate: reconnectMetaLeads, isPending: isReconnecting } = useReconnectMetaLeads({
		impersonationContext: userSession.impersonationContext,
		onError: ({ disconnected, error }) => (disconnected ? onReconnectStalled(error) : onError('Reconnect', error)),
		onSuccess: value => {
			if (isFacebookAuthUrl(value.authUrl)) {
				window.location.replace(value.authUrl);
			} else {
				invalidateMetaAdsQueries();
				onReconnectStalled({
					success: false,
					systemMessage: 'Invalid OAuth URL received',
				} as Api.IOperationResultNoValue);
			}
		},
	});
	const isChangingConnection = isReconnecting || isDisconnecting;

	// Handle OAuth callback redirect
	useEffect(() => {
		if (isPending) {
			return;
		}

		const pathname = history.location.pathname;
		if (pathname.includes('/success')) {
			toast.success('Meta Ads connected successfully!');
			invalidateMetaAdsProvider({ impersonationContext: userSession.impersonationContext });
			history.replace(pathname.replace('/success', ''));
		} else if (pathname.includes('/failure')) {
			toast.error('Failed to connect Meta Ads. Please try again.');
			history.replace(pathname.replace('/failure', ''));
		}
	}, [history, isPending, userSession.impersonationContext]);

	const hasAutoOpenedRef = useRef(false);

	// Auto-open modal if connected but not configured
	useEffect(() => {
		if (!isConnected) {
			hasAutoOpenedRef.current = false;
			return;
		}
		if (isConnected && !isConfigured && !hasAutoOpenedRef.current) {
			hasAutoOpenedRef.current = true;
			setIsModalOpen(true);
		}
	}, [isConnected, isConfigured]);

	const handleConnect = () => {
		connectMetaLeads();
	};

	const handleDisconnect = () => {
		setIsDisconnectConfirmationOpen(true);
	};

	const handleDisconnectConfirmed = (option?: IConfirmationDialogOption<boolean>) => {
		setIsDisconnectConfirmationOpen(false);
		if (option?.representedObject === true) {
			disconnectMetaLeads();
		}
	};

	const handleReconnect = () => {
		if (isChangingConnection) {
			return;
		}
		setIsModalOpen(false);
		setIsReconnectConfirmationOpen(true);
	};

	const handleReconnectConfirmed = (option?: IConfirmationDialogOption<boolean>) => {
		setIsReconnectConfirmationOpen(false);
		if (option?.representedObject === true) {
			reconnectMetaLeads();
		}
	};

	const reconnectNotice = reconnectReason ? (
		<div className={css(styleSheet.reconnectNotice)}>
			<MetaAuthFailureNotice
				reason={reconnectReason}
				title='Facebook stopped accepting your connection'
				onReconnect={handleReconnect}
				isReconnecting={isReconnecting}
				reconnectDisabled={isDisconnecting}
			/>
		</div>
	) : null;

	const reconnectConfirmation = (
		<ConfirmationDialog
			modalProps={{
				isOpen: isReconnectConfirmationOpen,
				onRequestClose: handleReconnectConfirmed,
			}}
			title='Reconnect Facebook Ads?'
			options={ReconnectConfirmationOptions}
		>
			Reconnecting disconnects Facebook Ads first, so you will choose your ad account and pages again after you sign
			back in.
		</ConfirmationDialog>
	);

	if (!userSession.isAdmin) {
		return <Redirect to='/integrations' />;
	}

	if (isPending) {
		return (
			<div className={css(styleSheet.container)}>
				<LoadingSpinner type='large' />
			</div>
		);
	}

	// If not connected, show connection prompt
	if (!isConnected) {
		return (
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.connectContainer)}>
					<figure>
						<MetaMainLogo />
					</figure>
					<h2 className={css(styleSheet.title)}>Connect to Facebook Ads</h2>
					<p className={css(integrationsStyleSheet.bodyText)}>
						Connect your Facebook account to sync lead ads and automatically import leads into Levitate.
					</p>
					<Button variant='primary' onClick={handleConnect} disabled={isConnecting} isLoading={isConnecting}>
						{isConnecting ? 'Connecting...' : 'Connect to Facebook'}
					</Button>
				</div>
			</div>
		);
	}

	// If connected and configured, show configuration summary
	if (isConfigured) {
		const selectedAccount = adAccounts?.find(account => account.id === metaAdsProvider.adAccountIds?.[0]);

		return (
			<>
				<div className={css(styleSheet.container)}>
					<div className={css(styleSheet.headerContainer)}>
						<figure className={css(integrationsStyleSheet.headerLogo)}>
							<MetaMainLogo />
						</figure>
						<h2 className={css(styleSheet.title)}>Ads Configuration</h2>
					</div>
					{reconnectNotice}

					<div className={css(styleSheet.configurationContainer)}>
						<div className={css(styleSheet.configSection)}>
							<h3 className={css(styleSheet.configSectionTitle)}>Ad Account</h3>
							{isLoadingAccounts ? (
								<LoadingLogo size={50} />
							) : (
								<div className={css(styleSheet.configValue)}>
									{selectedAccount?.name || metaAdsProvider.adAccountIds?.[0] || 'Unknown Account'}
									{selectedAccount?.businessName && (
										<span className={css(styleSheet.configSubValue)}>{selectedAccount.businessName}</span>
									)}
								</div>
							)}
						</div>

						<div className={css(styleSheet.configSection)}>
							<h3 className={css(styleSheet.configSectionTitle)}>Pages</h3>
							{isLoadingPages ? (
								<LoadingLogo size={50} />
							) : (
								<div className={css(styleSheet.configList)}>
									{metaAdsProvider.pageIds?.map(pageId => {
										const page = pages?.find(p => p.id === pageId);
										return (
											<div key={pageId} className={css(styleSheet.configValue)}>
												{page?.name || pageId}
												{page?.category && <span className={css(styleSheet.configSubValue)}>{page.category}</span>}
											</div>
										);
									})}
								</div>
							)}
						</div>

						<div className={css(styleSheet.buttonGroup)}>
							<Button variant='primary' onClick={() => setIsModalOpen(true)} disabled={isChangingConnection}>
								Edit Selections
							</Button>
							<Button
								variant='destructive-secondary'
								onClick={handleDisconnect}
								disabled={isChangingConnection}
								isLoading={isDisconnecting}
							>
								{isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
							</Button>
						</div>
					</div>
				</div>

				<FacebookAdsModal
					modalProps={{
						isOpen: isModalOpen,
						onRequestClose: () => {
							setIsModalOpen(false);
						},
					}}
					onSaveSuccess={() => {
						invalidateMetaAdsProvider({ impersonationContext: userSession.impersonationContext });
					}}
					onReconnect={handleReconnect}
				/>
				<ConfirmationDialog
					modalProps={{
						isOpen: isDisconnectConfirmationOpen,
						onRequestClose: handleDisconnectConfirmed,
					}}
					title='Disconnect Facebook Ads?'
					options={DisconnectConfirmationOptions}
				>
					Are you sure you want to disconnect Facebook Ads? This will stop syncing leads.
				</ConfirmationDialog>
				{reconnectConfirmation}
			</>
		);
	}

	// If connected but not configured, show the two-step setup flow
	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.headerContainer)}>
				<figure className={css(integrationsStyleSheet.headerLogo)}>
					<MetaMainLogo />
				</figure>
				<h2 className={css(styleSheet.title)}>Manage Meta Ads</h2>
			</div>
			{reconnectNotice}
			<footer className={css(bs.flex, bs.flexRow, bs.gap2)}>
				<Button variant='primary' onClick={() => setIsModalOpen(true)} disabled={isChangingConnection}>
					Edit Selections
				</Button>
				<Button
					variant='destructive-secondary'
					onClick={handleDisconnect}
					disabled={isChangingConnection}
					isLoading={isDisconnecting}
				>
					Disconnect
				</Button>
			</footer>
			<FacebookAdsModal
				modalProps={{
					isOpen: isModalOpen,
					onRequestClose: () => {
						setIsModalOpen(false);
					},
				}}
				onSaveSuccess={() => {
					invalidateMetaAdsProvider({ impersonationContext: userSession.impersonationContext });
				}}
				onReconnect={handleReconnect}
			/>
			<ConfirmationDialog
				modalProps={{
					isOpen: isDisconnectConfirmationOpen,
					onRequestClose: handleDisconnectConfirmed,
				}}
				title='Disconnect Facebook Ads?'
				options={DisconnectConfirmationOptions}
			>
				Are you sure you want to disconnect Facebook Ads? This will stop syncing leads.
			</ConfirmationDialog>
			{reconnectConfirmation}
		</div>
	);
};

export { ManageFacebookAds };
